import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "./AuthContext";
import { useLanguage } from "./LanguageContext";
import { fetchSchoolLanguage, type StudentSchoolLanguage } from "../modules/platform/api/schools";
import { updateProfile } from "../modules/platform/api/users";
import { normalizeAppLang, type AppLang } from "../i18n";
import { showErrorToast } from "../utils/errorHandler";
import { getPublicTenantIntent } from "../utils/publicTenantHandoff";

const SCHOOL_LANG_CACHE_PREFIX = "tkm_school_ui_lang:";

type SchoolLanguageContextValue = {
  language: AppLang;
  isLoading: boolean;
  /** Languages enabled by the school (allowed[]). */
  allowedLanguages: AppLang[];
  /** School ui_default. */
  schoolDefault: AppLang;
  /** Full GET /me/school-language payload when loaded. */
  schoolLanguageSettings: StudentSchoolLanguage | null;
  refresh: () => Promise<AppLang>;
  /**
   * Persist user preference (PATCH /me) then apply.
   * Rejects languages not in school allowed[].
   */
  setUserLanguage: (lng: AppLang) => Promise<void>;
};

const SchoolLanguageContext = createContext<SchoolLanguageContextValue | null>(null);

function cacheKey(schoolId: string) {
  return `${SCHOOL_LANG_CACHE_PREFIX}${schoolId}`;
}

function readCachedSchoolLang(schoolId: string): AppLang | null {
  try {
    const raw = sessionStorage.getItem(cacheKey(schoolId));
    return raw ? normalizeAppLang(raw) : null;
  } catch {
    return null;
  }
}

function writeCachedSchoolLang(schoolId: string, lang: AppLang) {
  try {
    sessionStorage.setItem(cacheKey(schoolId), lang);
  } catch {
    // ignore
  }
}

function normalizeAllowed(raw: string[] | null | undefined): AppLang[] {
  const out: AppLang[] = [];
  const seen = new Set<string>();
  for (const item of raw || []) {
    const code = normalizeAppLang(item);
    if (seen.has(code)) continue;
    seen.add(code);
    out.push(code);
  }
  return out.length ? out : (["en"] as AppLang[]);
}

/**
 * Priority: user preference (if in allowed) → school default → English.
 */
export function resolvePortalUiLanguage(opts: {
  userPreference?: string | null;
  schoolDefault?: string | null;
  allowed?: string[] | null;
}): AppLang {
  const allowed = normalizeAllowed(opts.allowed);
  const schoolDefault = normalizeAppLang(opts.schoolDefault || "en");
  const effectiveDefault = allowed.includes(schoolDefault) ? schoolDefault : allowed[0] || "en";
  if (opts.userPreference) {
    const pref = normalizeAppLang(opts.userPreference);
    if (allowed.includes(pref)) return pref;
  }
  return effectiveDefault;
}

/**
 * Portal-only provider: resolves UI language from user preference + school policy.
 * Do not wrap /dev/* routes with this provider.
 */
export const SchoolLanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading: authLoading, refreshUser } = useAuth();
  const { setLang } = useLanguage();
  const schoolId = user?.memberships?.[0]?.school_id || "";
  const userPref = user?.profile?.ui_language;

  const [language, setLanguage] = useState<AppLang>("en");
  const [allowedLanguages, setAllowedLanguages] = useState<AppLang[]>(["en"]);
  const [schoolDefault, setSchoolDefault] = useState<AppLang>("en");
  const [schoolLanguageSettings, setSchoolLanguageSettings] = useState<StudentSchoolLanguage | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(Boolean(schoolId) || authLoading);
  const inflight = useRef<Promise<AppLang> | null>(null);
  const setLangRef = useRef(setLang);
  setLangRef.current = setLang;

  const apply = useCallback(async (lng: AppLang) => {
    setLanguage(lng);
    await setLangRef.current(lng);
  }, []);

  const refresh = useCallback(async (): Promise<AppLang> => {
    if (!schoolId) {
      setAllowedLanguages(["en"]);
      setSchoolDefault("en");
      setSchoolLanguageSettings(null);
      await apply("en");
      setIsLoading(false);
      return "en";
    }
    if (inflight.current) return inflight.current;

    const run = (async () => {
      setIsLoading(true);
      try {
        const settings = await fetchSchoolLanguage();
        const allowed = normalizeAllowed(settings?.allowed);
        const def = normalizeAppLang(settings?.ui_default || "en");
        setAllowedLanguages(allowed);
        setSchoolDefault(def);
        setSchoolLanguageSettings(settings);
        const resolved = resolvePortalUiLanguage({
          userPreference: userPref,
          schoolDefault: def,
          allowed,
        });
        writeCachedSchoolLang(schoolId, resolved);
        await apply(resolved);
        return resolved;
      } catch {
        const cached = readCachedSchoolLang(schoolId) || "en";
        await apply(cached);
        return cached;
      } finally {
        setIsLoading(false);
        inflight.current = null;
      }
    })();

    inflight.current = run;
    return run;
  }, [schoolId, apply, userPref]);

  const setUserLanguage = useCallback(
    async (lng: AppLang) => {
      const next = normalizeAppLang(lng);
      if (!allowedLanguages.includes(next)) {
        showErrorToast(new Error("This language is not available for your school."));
        throw new Error("This language is not available for your school.");
      }
      try {
        await updateProfile({ ui_language: next });
        await refreshUser();
        if (schoolId) writeCachedSchoolLang(schoolId, next);
        await apply(next);
      } catch (err) {
        showErrorToast(err);
        throw err;
      }
    },
    [allowedLanguages, apply, refreshUser, schoolId]
  );

  useEffect(() => {
    if (authLoading) {
      setIsLoading(true);
      return;
    }
    if (!schoolId) {
      setIsLoading(false);
      const preAuthHint = getPublicTenantIntent();
      void apply(preAuthHint?.isMalay ? "ms" : "en");
      return;
    }
    const cached = readCachedSchoolLang(schoolId);
    if (cached) {
      void apply(cached);
    } else {
      const preAuthHint = getPublicTenantIntent();
      if (preAuthHint?.isMalay) {
        void apply("ms");
      }
    }
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schoolId, authLoading, userPref]);

  const value = useMemo(
    () => ({
      language,
      isLoading,
      allowedLanguages,
      schoolDefault,
      schoolLanguageSettings,
      refresh,
      setUserLanguage,
    }),
    [language, isLoading, allowedLanguages, schoolDefault, schoolLanguageSettings, refresh, setUserLanguage]
  );

  return (
    <SchoolLanguageContext.Provider value={value}>{children}</SchoolLanguageContext.Provider>
  );
};

export const useSchoolLanguage = () => {
  const ctx = useContext(SchoolLanguageContext);
  if (!ctx) {
    throw new Error("useSchoolLanguage must be used inside SchoolLanguageProvider");
  }
  return ctx;
};

/** Safe hook: returns null outside portal provider (e.g. DevTeam routes). */
export const useSchoolLanguageOptional = () => useContext(SchoolLanguageContext);
